import { Controller } from "@hotwired/stimulus"
import type { TurboBeforeFetchRequestEvent, TurboSubmitEndEvent, TurboSubmitStartEvent } from "@hotwired/turbo"
import Confirmation from "../../confirmation"
import SelectionToolbarController from "../selection_toolbar_controller"
import type SearchFormController from "../search_form_controller"

export default class ListController extends Controller {
  static outlets = ["search-form"]
  static targets = [
    "archiveSelectedBtn",
    "markReadBtn",
    "markUnreadBtn",
    "toolbar"
  ]

  declare searchFormOutlet: SearchFormController
  declare hasSearchFormOutlet: boolean
  declare archiveSelectedBtnTarget: HTMLButtonElement
  declare markReadBtnTarget: HTMLButtonElement
  declare markUnreadBtnTarget: HTMLButtonElement
  declare toolbarTarget: HTMLDivElement

  archiveSelectedBtnTargetConnected(el: HTMLButtonElement) {
    const form = el.closest("form")
    if (!form) {
      return
    }

    form.addEventListener("turbo:before-fetch-request", async (event: TurboBeforeFetchRequestEvent) => {
      event?.preventDefault()

      if (await Confirmation.confirmBulkArchive("responses")) {
        event.detail.resume(null)
      } else {
        const selectionToolbar = SelectionToolbarController.getInstance(this.toolbarTarget)
        selectionToolbar?.clearSelected()
      }
    })

    form.addEventListener("turbo:submit-start", (event: TurboSubmitStartEvent) => {
      const selectionToolbar = SelectionToolbarController.getInstance(this.toolbarTarget)
      const ids = selectionToolbar?.selectedIds() || []
      ids.forEach(id => {
        event.detail.formSubmission.fetchRequest.body
          .append("ids[]", id)
      })
    })

    form.addEventListener("turbo:submit-end", (event: TurboSubmitEndEvent) => {
      if (event.detail.success) {
        const selectionToolbar = SelectionToolbarController.getInstance(this.toolbarTarget)
        selectionToolbar?.clearSelected()
      }
    })
  }

  markReadBtnTargetConnected(el: HTMLButtonElement) {
    this.setupBulkActionForm(el)
  }

  markUnreadBtnTargetConnected(el: HTMLButtonElement) {
    this.setupBulkActionForm(el)
  }

  private setupBulkActionForm(el: HTMLButtonElement) {
    const form = el.closest("form")
    if (!form) {
      return
    }

    form.addEventListener("turbo:submit-start", (event: TurboSubmitStartEvent) => {
      const selectionToolbar = SelectionToolbarController.getInstance(this.toolbarTarget)
      const ids = selectionToolbar?.selectedIds() || []
      ids.forEach(id => {
        event.detail.formSubmission.fetchRequest.body
          .append("ids[]", id)
      })
    })

    form.addEventListener("turbo:submit-end", (event: TurboSubmitEndEvent) => {
      if (event.detail.success) {
        const selectionToolbar = SelectionToolbarController.getInstance(this.toolbarTarget)
        selectionToolbar?.clearSelected()
      }
    })
  }

  async confirmArchive(event: TurboBeforeFetchRequestEvent) {
    event.preventDefault()

    if (await Confirmation.confirmArchive("this response")) {
      event.detail.resume(null)
    }
  }

  appendSearchParams(event: TurboSubmitStartEvent) {
    if (!this.hasSearchFormOutlet) return

    const formData = new FormData(this.searchFormOutlet.formTarget)
    formData.forEach((value, key) => {
      if (typeof value === "string") {
        event.detail.formSubmission.fetchRequest.body.append(key, value)
      }
    })
  }

  getSelectionToolbarController(): SelectionToolbarController | null {
    return SelectionToolbarController.getInstance(this.toolbarTarget)
  }
}
